import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
// Consolidated component imports via root barrel
import { 
  ScoutIQDashboard
} from '../components';
import DataIntegrityPanel from '../components/debug/DataIntegrityPanel';
import { ONBOARDING_V2_ENABLED } from '../config/featureFlags';
import { shouldShowOnboarding } from '../utils/onboardingEligibility';
import { usePreferences } from '../contexts/PreferencesContext';
import TutorialVideoButton from '../components/common/TutorialVideoButton';
import BrandedLoadingScreen from '../components/common/BrandedLoadingScreen';
import './Dashboard.css';

export default function Dashboard() {
  const [selectedCourse, setSelectedCourse] = useState(null); // selection for ScoutIQ tab (toggle)
  const [loadingCourses, setLoadingCourses] = useState(false);
  const { user } = useAuth();
  const { preferences: uiPreferences } = usePreferences();
  const navigate = useNavigate();
  const { refresh: refreshSubscription } = useSubscription();
  const { state, selectors, courses, getFilteredEndeavors, getFilteredAssignments, hydrateDashboard, isDashboardHydrated, getDocumentsByUser } = useData();
  
  // ✅ NEW: Courses now come directly from DataContext - they persist across navigation!
  const userId = user?.id || user?.userId;
  // Unified hydration spinner state — initialize to true when dashboard is NOT yet hydrated
  // to prevent a brief flash of dashboard content before the splash screen appears.
  const [hydrating, setHydrating] = useState(() => !(typeof isDashboardHydrated === 'function' && isDashboardHydrated()));
  const location = useLocation();
  const [highlightedCourseId, setHighlightedCourseId] = useState(null);
  // ✅ Guard against duplicate fetchCourseIds calls during re-renders
  const fetchInFlightRef = useRef(false);

  // Stabilize DataContext method references to prevent callback recreation
  const getFilteredEndeavorsRef = useRef(getFilteredEndeavors);
  const getFilteredAssignmentsRef = useRef(getFilteredAssignments);
  const getDocumentsByUserRef = useRef(getDocumentsByUser);
  useEffect(() => {
    getFilteredEndeavorsRef.current = getFilteredEndeavors;
    getFilteredAssignmentsRef.current = getFilteredAssignments;
    getDocumentsByUserRef.current = getDocumentsByUser;
  }, [getFilteredEndeavors, getFilteredAssignments, getDocumentsByUser]);

  // Onboarding V2: redirect to /onboarding if user hasn't completed it
  useEffect(() => {
    if (!ONBOARDING_V2_ENABLED || !user) return;
    // Wait for preferences and dashboard data to load before deciding
    if (uiPreferences === undefined || uiPreferences === null) return;
    if (!isDashboardHydrated) return;
    if (shouldShowOnboarding({ user, uiPreferences: uiPreferences || {}, hasCourses: courses && courses.length > 0 })) {
      navigate('/onboarding', { replace: true });
    }
  }, [user, uiPreferences, isDashboardHydrated, courses, navigate]);

  // Handle checkout success return - refresh subscription and clean URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutSuccess = params.get('checkout') === 'success';
    
    if (checkoutSuccess) {
      // Trigger subscription refresh to pick up new plan
      if (typeof refreshSubscription === 'function') {
        refreshSubscription();
      }
      
      // Clean up URL parameters
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [location.search, refreshSubscription]);

  // Kick off dashboard hydration ASAP once user known (bulk prefetch)
  // NOTE: isDashboardHydrated is intentionally NOT in deps — it's an inline ref-getter
  // that gets a new identity on every DataContext render, which would cause constant
  // effect re-runs and cancelled-flag races that restart the loading animation.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!userId || !hydrateDashboard) return;
      // Avoid re-trigger if already hydrated
      if (typeof isDashboardHydrated === 'function' && isDashboardHydrated()) {
        setHydrating(false);
        return;
      }
      try {
        setHydrating(true);
        await hydrateDashboard(userId);
      } catch (e) {
        console.warn('Dashboard: hydrateDashboard failed', e);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    }
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, hydrateDashboard]);

  const fetchCourseIds = useCallback(async () => {
    if (!userId || !getFilteredEndeavorsRef.current) return;
    // ✅ Prevent overlapping calls during re-render cascades
    if (fetchInFlightRef.current) {
      console.log('[Dashboard] fetchCourseIds already in flight, skipping');
      return;
    }
    fetchInFlightRef.current = true;
    setLoadingCourses(true);
    try {
      console.log('[Dashboard] Fetching course IDs from DataContext...');
      const list = await getFilteredEndeavorsRef.current(userId);
      
      const ids = list.map(e => e.endeavorId || e.id).filter(Boolean);
      console.log('[Dashboard] Fetched', ids.length, 'course IDs');
      // ✅ No longer setting courses state - DataContext provides enriched courses array!
    } catch (err) {
      console.error('[Dashboard] Failed to fetch course IDs:', err);
    } finally {
      setLoadingCourses(false);
      fetchInFlightRef.current = false; // ✅ Release the lock
    }
  }, [userId]);

  useEffect(() => { fetchCourseIds(); }, [fetchCourseIds]);

  // Refetch courses when navigating back to dashboard to ensure fresh data
  const previousLocationRef = useRef(location.pathname);
  useEffect(() => {
    // If we navigated TO /dashboard from somewhere else, refetch course IDs
    if (location.pathname === '/dashboard' && previousLocationRef.current !== '/dashboard') {
      console.log('[Dashboard] Navigated back to dashboard from', previousLocationRef.current);
      console.log('[Dashboard] Current courses from DataContext:', courses.length, 'courses available');
      // Only refetch if we don't have courses yet (DataContext should persist them)
      if (courses.length === 0) {
        console.log('[Dashboard] No courses in DataContext, fetching...');
        fetchCourseIds();
      } else {
        console.log('[Dashboard] Using cached courses from DataContext');
      }
    }
    previousLocationRef.current = location.pathname;
  }, [location.pathname, fetchCourseIds, courses.length]);

  // If navigation state requests a highlight (e.g., after create), apply it briefly
  useEffect(() => {
    try {
      const id = location?.state?.highlightCourseId;
      if (id) {
        setHighlightedCourseId(id);
        // Also refresh list in case caller didn't dispatch event
        fetchCourseIds();
        const t = setTimeout(() => setHighlightedCourseId(null), 3000);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // ignore
    }
    return undefined;
  }, [location?.state?.highlightCourseId, fetchCourseIds]);

  // Listen for external requests to select/highlight an endeavor (e.g., after create)
  useEffect(() => {
    const handleSelectEndeavor = async (event) => {
      try {
        const id = event?.detail?.id;
        if (!id) return;
        // Ensure freshest data
        await fetchCourseIds();

        // Highlight the course card briefly but do NOT select it
        setHighlightedCourseId(id);
        const t = setTimeout(() => setHighlightedCourseId(null), 3000);
        return () => clearTimeout(t);
      } catch (e) {
        console.warn('Dashboard: handleSelectEndeavor failed', e);
      }
    };

    window.addEventListener('scoutiq:selectEndeavor', handleSelectEndeavor);
    // Programmatic open of course view
    const handleOpenCourseView = async (event) => {
      try {
        const id = event?.detail?.id;
        if (!id) return;
        // Ensure freshest list
        await fetchCourseIds();

        // Try to find full course object in our cached courses
        const found = (courses || []).find(c => {
          const cid = c?.id || c?.endeavorId || c?.courseId || c?.courseID || c?.endeavorID;
          return String(cid) === String(id);
        });

        if (found) {
          setSelectedCourse(found);
        } else {
          // Fallback: set minimal selection object so ScoutIQDashboard will open view
          setSelectedCourse({ id });
        }

        // Also ensure highlight and refreshed list
        setHighlightedCourseId(id);
        const t = setTimeout(() => setHighlightedCourseId(null), 3000);
        return () => clearTimeout(t);
      } catch (e) {
        console.warn('Dashboard: handleOpenCourseView failed', e);
      }
    };

    window.addEventListener('scoutiq:openCourseView', handleOpenCourseView);
    return () => {
      window.removeEventListener('scoutiq:selectEndeavor', handleSelectEndeavor);
      window.removeEventListener('scoutiq:openCourseView', handleOpenCourseView);
    };
  }, [fetchCourseIds, courses]);

  // Merge progress into courses using selectors (re-computes on dataVersion change)
  const coursesWithProgress = useMemo(() => {
    if (!courses || courses.length === 0) return [];
    return courses.map(c => {
      const eid = c.endeavorId || c.id;
      // Use canonical selector for progress
      const progress = selectors.selectCourseProgress(state, eid);
      const progressPercent = progress.percent ?? c.progressPercent ?? c.progress ?? null;
      return progressPercent == null ? c : { ...c, progressPercent };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, selectors, state]);

  // No restore of selection: entering ScoutIQ starts with none selected.

  /* courseCardsGrid unused - virtualization handled in render
  const courseCardsGrid = useMemo(() => {
    if (loadingCourses) {
      return (
        <div className="loading-state" style={{height:'40vh'}}>
          <div className="loading-spinner"></div>
          <p>Loading courses...</p>
        </div>
      );
    }
    if (!courseIds || courseIds.length === 0) {
      return (
        <div style={{padding:'1rem', fontSize:'.8rem', color:'var(--text-muted,#64748b)'}}>No courses yet. Create a course to see it here.</div>
      );
    }
    const atTop = rowOffset === 0;
    const atBottom = rowOffset === maxRowOffset || windowedIds.length < WINDOW_SIZE;

    return (
      <div className="course-cards-grid-wrapper course-cards-grid-wrapper--paged">
        <div className="course-cards-grid force-responsive" style={{
          gridAutoRows: 'minmax(var(--course-card-min-height, 360px), 1fr)'
        }}>
          {windowedIds.map(id => (
            <CourseCard key={id} courseId={id} />
          ))}
        </div>
        {courseIds.length > WINDOW_SIZE && (
          <div className="course-grid-pager" aria-label="Course grid pager" role="group">
            <button
              type="button"
              className="course-grid-pager-btn"
              onClick={handlePageUp}
              disabled={atTop}
              aria-label="Scroll up one row"
            >
              <span aria-hidden="true" className="chevron chevron-up">▲</span>
            </button>
            <div className="course-grid-pager-status" aria-live="polite">
              <span>{rowOffset + 1}</span>/<span>{maxRowOffset + 1}</span>
            </div>
            <button
              type="button"
              className="course-grid-pager-btn"
              onClick={handlePageDown}
              disabled={atBottom}
              aria-label="Scroll down one row"
            >
              <span aria-hidden="true" className="chevron chevron-down">▼</span>
            </button>
          </div>
        )}
      </div>
    );
  }, [courseIds, loadingCourses, windowedIds, rowOffset, maxRowOffset, handlePageUp, handlePageDown, WINDOW_SIZE]);
  */

  // Selection handler for ScoutIQ dashboard column (TODO: feed into center/desk panels later)
  const handleSelectCourse = useCallback((course) => {
    setSelectedCourse(prev => {
      const prevId = prev?.id || prev?.endeavorId;
      const nextId = course?.id || course?.endeavorId;
      if (prevId && nextId && prevId === nextId) return null; // toggle off
      return course;
    });
  }, []);

  /* handleTabsChange unused - tabs removed
  const handleTabsChange = useCallback((idx) => {
    // Clear selection when entering ScoutIQ tab (now index 0) for a fresh view
    if (idx === 0) setSelectedCourse(null);
    
    // Track tab change
    try {
      const tabName = idx === 0 ? 'ScoutIQ' : 'Courses Grid';
      UsageEventsTracker.recordUserAction('TAB_CHANGE', {
        action: 'click',
        category: 'navigation',
        entityType: 'tab',
        entityId: tabName.toLowerCase().replace(/\s+/g, '-'),
        label: `Dashboard ${tabName} Tab`,
        metadata: { tabIndex: idx, tabName }
      }, { userId: user?.id || user?.userId });
    } catch (trackError) {
      console.warn('Dashboard: Failed to track tab change', trackError);
    }
  }, [user]);
  */

  // Migrate old tab indices from 3-tab layout to 2-tab layout
  useEffect(() => {
    const storageKey = 'dashboard.tabs.index';
    const oldIndex = localStorage.getItem(storageKey);
    if (oldIndex != null) {
      const num = parseInt(oldIndex, 10);
      // Old layout: Overview (0), Courses Grid (1), ScoutIQ (2)
      // New layout: ScoutIQ (0), Courses Grid (1)
      if (num === 0 || num === 2) {
        // Overview or old ScoutIQ → new ScoutIQ (index 0)
        localStorage.setItem(storageKey, '0');
      } else if (num === 1) {
        // Courses Grid stays at index 1
        localStorage.setItem(storageKey, '1');
      }
    }
  }, []);

  useEffect(() => {
    UsageEventsTracker.recordQueued({
      eventType: 'PAGE_VIEW',
      payload: { action:'view', category:'navigation', entityType:'page', entityId:'/dashboard', label:'Dashboard' }
    });
  }, []);

  // Demo toast for QA / design review: append ?demoToast=1 to URL
  const { addToast } = useToast();
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('demoToast') === '1') {
        addToast({
          intent: 'info',
          message: 'Welcome! This is a demo toast. Remove ?demoToast=1 from the URL to hide.',
          duration: 6000,
          action: {
            label: 'Got it',
            onClick: () => console.log('Demo toast acknowledged')
          }
        });
      }
    } catch (err) {
      // no-op
    }
  }, [addToast]);

  // Show branded loading screen before any dashboard content mounts (prevents per-card flicker)
  // hydrating state is initialized based on isDashboardHydrated(), so this single check is sufficient.
  if (hydrating) {
    return <BrandedLoadingScreen />;
  }

  return (
    <div className="dashboard dashboard--compact">
      <div className="main-dashboard-container">
        <ScoutIQDashboard
          courses={coursesWithProgress}
          loadingCourses={loadingCourses}
          selectedCourseId={selectedCourse?.id || selectedCourse?.endeavorId}
          selectedCourse={selectedCourse}
          onSelectCourse={handleSelectCourse}
          highlightCourseId={highlightedCourseId}
        />
      </div>
      
      {/* Dev-only integrity panel */}
      <DataIntegrityPanel selectedCourseId={selectedCourse?.id || selectedCourse?.endeavorId} />

      <TutorialVideoButton />
    </div>
  );
}